/**
 * Google Drive Helper Utilities
 * 
 * Helper functions for working with Google Drive download links
 */

/**
 * Extracts the file ID from a Google Drive share link
 * 
 * Supported formats:
 * - https://drive.google.com/file/d/FILE_ID/view
 * - https://drive.google.com/open?id=FILE_ID
 * - https://drive.google.com/uc?id=FILE_ID
 * 
 * @param url Google Drive share URL
 * @returns File ID or null if not found
 */
export function extractGoogleDriveFileId(url: string): string | null {
    if (!url) return null;

    try {
        // Pattern 1: /file/d/FILE_ID/
        const pattern1 = /\/file\/d\/([a-zA-Z0-9_-]+)/;
        const match1 = url.match(pattern1);
        if (match1 && match1[1]) {
            return match1[1];
        }

        // Pattern 2: ?id=FILE_ID or &id=FILE_ID
        const pattern2 = /[?&]id=([a-zA-Z0-9_-]+)/;
        const match2 = url.match(pattern2);
        if (match2 && match2[1]) {
            return match2[1];
        }

        // Pattern 3: /d/FILE_ID/ or /d/FILE_ID?
        const pattern3 = /\/d\/([a-zA-Z0-9_-]+)[/?]/;
        const match3 = url.match(pattern3);
        if (match3 && match3[1]) {
            return match3[1];
        }

        return null;
    } catch (error) {
        console.error('[GoogleDriveHelper] Error extracting file ID:', error);
        return null;
    }
}

/**
 * Converts a Google Drive share link to a direct download link
 * 
 * @param shareUrl Google Drive share URL
 * @returns Direct download URL or null if invalid
 */
export function convertToDirectDownloadUrl(shareUrl: string): string | null {
    const fileId = extractGoogleDriveFileId(shareUrl);
    
    if (!fileId) {
        return null;
    }

    // Use the export=download parameter for direct downloads
    return `https://drive.google.com/uc?export=download&id=${fileId}`;
}

/**
 * Validates if a URL is a valid Google Drive link
 * 
 * @param url URL to validate
 * @returns true if valid Google Drive URL
 */
export function isValidGoogleDriveUrl(url: string): boolean {
    if (!url) return false;
    
    const drivePatterns = [
        /drive\.google\.com/,
        /docs\.google\.com/
    ];

    return drivePatterns.some(pattern => pattern.test(url));
}

/**
 * Gets file metadata from Google Drive (requires public access)
 * 
 * @param fileId Google Drive file ID
 * @returns Promise with file metadata
 */
export async function getGoogleDriveFileMetadata(fileId: string): Promise<{
    name: string;
    size: string;
    mimeType: string;
} | null> {
    try {
        // This requires the file to be publicly accessible
        // In a production environment, you'd use the Google Drive API with proper authentication
        const url = `https://www.googleapis.com/drive/v3/files/${fileId}?fields=name,size,mimeType&supportsAllDrives=true`;
        
        const response = await fetch(url);
        
        if (!response.ok) {
            throw new Error('Failed to fetch file metadata');
        }

        const data = await response.json();
        
        return {
            name: data.name || 'Unknown',
            size: formatFileSize(parseInt(data.size || '0')),
            mimeType: data.mimeType || 'application/octet-stream'
        };
    } catch (error) {
        console.error('[GoogleDriveHelper] Error fetching metadata:', error);
        return null;
    }
}

/**
 * Formats file size in bytes to human-readable format
 * 
 * @param bytes File size in bytes
 * @returns Formatted file size string
 */
function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Downloads a file from Google Drive
 * 
 * @param fileId Google Drive file ID
 * @param filename Desired filename for download
 * @returns Promise that resolves when download starts
 */
export async function downloadGoogleDriveFile(fileId: string, filename: string = 'download.apk'): Promise<void> {
    const downloadUrl = `https://drive.google.com/uc?export=download&id=${fileId}`;
    
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.target = '_blank';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Small delay to ensure download starts
    await new Promise(resolve => setTimeout(resolve, 500));
}

/**
 * Example usage and testing
 */
export const GoogleDriveHelperExample = {
    // Example share URLs
    shareUrls: [
        'https://drive.google.com/file/d/1abc123xyz456/view?usp=sharing',
        'https://drive.google.com/open?id=1abc123xyz456',
        'https://drive.google.com/uc?id=1abc123xyz456&export=download'
    ],
    
    // Convert all to direct download URLs
    convertExamples: function() {
        return this.shareUrls.map(url => ({
            original: url,
            converted: convertToDirectDownloadUrl(url)
        }));
    }
};
